// ----------------------------------------------------------------------------
// initLegacyBucket — shared bucket integration for azathoth legacy apps.
//
// This module encapsulates the boilerplate that xtrc.js and wavedit.js each
// duplicate: BucketService creation, polling, multi-bucket tabs, text input
// modal, bucket manager UI, export modal, and the outer split-grid gutter.
//
// Usage:
//   const { bucket, exportModal } = require('./legacy-bucket-integration.js')({
//      appName: 'framestack',          // used for localStorage keys
//      bucketContent: '.bucketcontent',// selector for bucket content div
//      bucketPanel: '.xtrc-bucket-panel', // selector for bucket panel
//      onBucketDrop: (e) => {},        // optional: handle drop onto bucket
//      onItemDrop: (item) => {},       // optional: handle bucket item dropped onto app
//   });
// ----------------------------------------------------------------------------

const { BucketService, BucketManager, dbGetAll } = require('../../../../shared/src/ui/bucket-export/bucket.js');
const { createExportModal } = require('../../../../shared/src/ui/bucket-export/export-modal.js');
const { createBucketManagerUI } = require('../../../../shared/src/ui/bucket-export/bucket-manager-ui.js');
const { openVideoPlayer, closeVideoPlayer, openImageViewer, closeImageViewer } = require('../../../../shared/src/ui/video-player.cjs');
const { setupBucketSync } = require('../common/bucket-sync.js');
const SplitModule = require('split-grid');
const Split = SplitModule.default || SplitModule;

// Styled confirm modal from qs.js (falls back to native confirm if unavailable)
let confirmmessage = null;
try {
   const qsLib = require('../../libs/qs');
   confirmmessage = qsLib.confirmmessage || null;
} catch (_) {}
const styledConfirm = confirmmessage || ((msg) => Promise.resolve(confirm(msg)));

// Expose video/image viewer modals on window so bucket.js can use them
// (bucket.js checks window.openImageViewer / window.openVideoPlayer)
if (typeof window !== 'undefined') {
   if (!window.openVideoPlayer) window.openVideoPlayer = openVideoPlayer;
   if (!window.closeVideoPlayer) window.closeVideoPlayer = closeVideoPlayer;
   if (!window.openImageViewer) window.openImageViewer = openImageViewer;
   if (!window.closeImageViewer) window.closeImageViewer = closeImageViewer;
}

// Prevent auto-download of recording files — recordings should only go to bucket.
// Intercepts <a> clicks where the download attribute matches known recording prefixes.
if (typeof document !== 'undefined' && !document.__bucketRecDownloadIntercepted) {
   document.__bucketRecDownloadIntercepted = true;
   document.addEventListener('click', (e) => {
      const a = e.target.closest && e.target.closest('a[download]');
      if (!a) return;
      const prefixes = ['KAEPS_REC', 'J2W_REC', 'AMEN_REC', 'SPECTROPAINT_REC', 'JPEG2WAV_REC', 'GRANULR_REC', 'SYNAPS_REC', 'XCONV_REC', 'spectropaint', 'CELLAUTOMATA_REC', 'PATCHMAT_REC', 'STRETCHCATHEDRAL_REC', 'UVB666_REC', 'IMGBEND_REC', 'CHAOSCILLATOR_REC', 'BINSYNTH_REC'];
      if (prefixes.some(p => a.download && a.download.startsWith(p))) {
         e.preventDefault();
         e.stopImmediatePropagation();
      }
   }, true);
}

function initLegacyBucket(opts = {}) {
   const {
      appName = 'legacy',
      bucketContentSelector = '.bucketcontent',
      bucketPanelSelector = '.xtrc-bucket-panel',
      onBucketDrop = null,
      onItemDrop = null,
   } = opts;

   // ---- DOM refs ----
   const bucketContentEl = document.querySelector(bucketContentSelector);
   const bucketPanelEl = document.querySelector(bucketPanelSelector);
   const dlBucketBtn = document.querySelector('#dlbucket');
   const clearBucketBtn = document.querySelector('#clearbucket');

   if (!bucketContentEl || !bucketPanelEl) {
      return { bucket: null, exportModal: null };
   }

   // ---- BucketService ----
   const bucket = new BucketService({
      container: bucketContentEl,
      panelEl: bucketPanelEl,
      statusFn: () => {},
      confirmFn: styledConfirm,
   });
   bucket.load();

   // ---- Sync bucket changes across tabs ----
   // Replaces the old 1s IndexedDB poll that caused periodic jank in Firefox.
   setupBucketSync({ bucket, dbGetAll });

   // ---- Export modal ----
   const exportModal = createExportModal(bucket);

   if (dlBucketBtn) {
      dlBucketBtn.addEventListener('click', () => {
         if (dlBucketBtn.classList.contains('export-panel-open')) {
            exportModal.close();
         } else {
            exportModal.open();
         }
      });
   }
   if (clearBucketBtn) {
      clearBucketBtn.addEventListener('click', async () => {
         if (!bucket.items.length) return;
         const ok = await styledConfirm(`Clear ${bucket.items.length} item(s) from bucket?`);
         if (ok) bucket.clear();
      });
   }

   // ---- View mode toggle (grid / list) ----
   const viewToggleBtn = document.getElementById('bucket-view-toggle');
   if (viewToggleBtn) {
      // Sync initial button state from bucket's viewMode
      const mode = bucket.viewMode || 'grid';
      viewToggleBtn.textContent = mode === 'list' ? '\u2630' : '\u25A6';
      viewToggleBtn.title = mode === 'list' ? 'Switch to grid view' : 'Switch to list view';
      viewToggleBtn.setAttribute('aria-pressed', mode === 'list' ? 'true' : 'false');
      viewToggleBtn.addEventListener('click', () => {
         bucket.setViewMode(bucket.viewMode === 'list' ? 'grid' : 'list');
      });
   }

   // ---- Drag/drop onto bucket panel ----
   ['dragenter', 'dragover'].forEach((evt) => {
      bucketPanelEl.addEventListener(evt, (e) => {
         e.preventDefault();
         if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
         bucketPanelEl.classList.add('dragover');
      });
   });
   bucketPanelEl.addEventListener('dragleave', (e) => {
      if (e.target === bucketPanelEl) bucketPanelEl.classList.remove('dragover');
   });
   bucketPanelEl.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      bucketPanelEl.classList.remove('dragover');

      // If dragging an item that's already in this bucket, don't re-add it
      const bucketItemId = e.dataTransfer.getData('application/x-bucket-item-id');
      if (bucketItemId && bucket && bucket.items.some(it => it.id === bucketItemId)) {
         return;
      }

      // ---- OS filesystem file drop (drag files from computer) ----
      // Supports multiple files: audio, MIDI, module (MOD/XM/IT/S3M), video, images.
      // Falls through to custom MIME type handlers below if no OS files are present.
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0 && bucket) {
         addOsFilesToBucket(e.dataTransfer.files, bucket, appName);
         return;
      }

      // Generic image drop handler (from export modal RBM image slots, etc.)
      const imageUrl = e.dataTransfer.getData('application/x-image-url');
      const imageName = e.dataTransfer.getData('application/x-image-name') || e.dataTransfer.getData('text/plain') || `image_${Date.now()}.png`;
      if (imageUrl && bucket) {
         fetch(imageUrl)
            .then(r => r.blob())
            .then(blob => blob.arrayBuffer())
            .then(data => {
               return bucket.add({
                  source: appName,
                  name: imageName,
                  imageData: data,
                  imageFormat: 'image/png',
                  imageMime: 'image/png',
                  fileType: 'image'
               });
            })
            .catch(() => {});
         return;
      }

      // Generic audio drop handler (from export modal RBM sample slots, etc.)
      const audioUrl = e.dataTransfer.getData('application/x-module-url');
      const audioName = e.dataTransfer.getData('application/x-module-sample-name') || e.dataTransfer.getData('text/plain') || `sample_${Date.now()}.wav`;
      if (audioUrl && bucket) {
         fetch(audioUrl)
            .then(r => r.blob())
            .then(blob => blob.arrayBuffer())
            .then(data => {
               return bucket.add({
                  source: appName,
                  name: audioName,
                  wavLike: data,
                  fileType: 'wav'
               });
            })
            .catch(() => {});
         return;
      }

      // MIDI drop handler
      const midiUrl = e.dataTransfer.getData('application/x-midi-url');
      const midiName = e.dataTransfer.getData('application/x-midi-sample-name') || e.dataTransfer.getData('text/plain') || `midi_${Date.now()}.mid`;
      if (midiUrl && bucket) {
         fetch(midiUrl)
            .then(r => r.blob())
            .then(blob => blob.arrayBuffer())
            .then(data => bucket.add({ source: appName, name: midiName, midiLike: data }))
            .catch(() => {});
         return;
      }

      // Video drop handler
      const videoUrl = e.dataTransfer.getData('application/x-video-url');
      const videoName = e.dataTransfer.getData('application/x-video-name') || e.dataTransfer.getData('text/plain') || `video_${Date.now()}.webm`;
      if (videoUrl && bucket) {
         fetch(videoUrl)
            .then(r => r.blob())
            .then(blob => blob.arrayBuffer())
            .then(data => bucket.add({ source: appName, name: videoName, videoData: data, fileType: 'video' }))
            .catch(() => {});
         return;
      }

      // ---- Dashboard file drop handler (drag from Files tab → bucket) ----
      const dashFileId = e.dataTransfer.getData('application/x-dash-file-id');
      if (dashFileId && bucket) {
         const fileUrl = '/dashboard/files/serve/' + dashFileId;
         fetch(fileUrl)
            .then(r => {
               if (!r.ok) throw new Error('fetch failed');
               // Get content-type from headers
               const ct = r.headers.get('content-type') || 'application/octet-stream';
               // Get filename from content-disposition if available
               const cd = r.headers.get('content-disposition') || '';
               let fname = `file_${dashFileId.slice(0, 8)}`;
               if (cd) {
                  const m = cd.match(/filename="?(.+?)"?$/);
                  if (m) fname = decodeURIComponent(m[1]);
               }
               return Promise.all([r.arrayBuffer(), ct, fname]);
            })
            .then(([data, ct, fname]) => {
               // Determine bucket item type from content-type
               let fileType = 'wav';
               let addOpts = { source: appName, name: fname };
               if (ct.startsWith('image/')) {
                  addOpts.imageData = data; addOpts.imageFormat = ct; addOpts.imageMime = ct; addOpts.fileType = 'image';
               } else if (ct.startsWith('video/')) {
                  addOpts.videoData = data; addOpts.fileType = 'video';
               } else if (ct.includes('midi') || fname.match(/\.(mid|midi)$/i)) {
                  addOpts.midiLike = data;
               } else if (ct.startsWith('audio/')) {
                  addOpts.wavLike = data; addOpts.fileType = 'wav';
               } else {
                  // Generic binary — store as wav-like for non-audio too
                  addOpts.wavLike = data; addOpts.fileType = 'wav';
               }
               return bucket.add(addOpts);
            })
            .catch(() => {});
         return;
      }

      if (onBucketDrop) onBucketDrop(e);
   });

   // ---- textInputModal helper ----
   function textInputModal({ title = 'Enter value', placeholder = '', defaultValue = '' } = {}) {
      return new Promise((resolve) => {
         const modal = document.getElementById('text-input-modal');
         const titleEl = document.getElementById('tim-title');
         const inputEl = document.getElementById('tim-input');
         const closeBtn = document.getElementById('tim-close');
         const cancelBtn = document.getElementById('tim-cancel');
         const confirmBtn = document.getElementById('tim-confirm');

         if (!modal) { resolve(prompt(title, defaultValue) || null); return; }

         titleEl.textContent = title;
         inputEl.placeholder = placeholder;
         inputEl.value = defaultValue;
         modal.removeAttribute('hidden');
         inputEl.focus();
         inputEl.select();

         function close(value = null) {
            modal.setAttribute('hidden', '');
            closeBtn.removeEventListener('click', onClose);
            cancelBtn.removeEventListener('click', onCancel);
            confirmBtn.removeEventListener('click', onConfirm);
            inputEl.removeEventListener('keydown', onKey);
            resolve(value);
         }
         function onClose() { close(null); }
         function onCancel() { close(null); }
         function onConfirm() { close(inputEl.value.trim() || null); }
         function onKey(e) {
            if (e.key === 'Enter') onConfirm();
            if (e.key === 'Escape') onClose();
         }

         closeBtn.addEventListener('click', onClose);
         cancelBtn.addEventListener('click', onCancel);
         confirmBtn.addEventListener('click', onConfirm);
         inputEl.addEventListener('keydown', onKey);
      });
   }

   // ---- Multi-bucket tabs ----
   const ACTIVE_BUCKET_KEY = `${appName}-active-bucket`;

   (function initBucketTabs() {
      const tabsEl = document.getElementById('bucket-tabs');
      const addBtn = document.getElementById('bucket-tab-add');
      if (!tabsEl || !addBtn) return;

      function renderTabs(buckets) {
         tabsEl.innerHTML = '';
         const openBuckets = buckets.filter((b) => b.isOpen !== false);
         for (const bkt of openBuckets) {
            const tab = document.createElement('div');
            tab.className = 'bucket-tab' + (bkt.id === bucket.bucketId ? ' active' : '');
            tab.dataset.id = bkt.id;

            const label = document.createElement('span');
            label.className = 'bucket-tab-label';
            label.textContent = bkt.name;
            label.title = 'Double-click to rename';
            label.addEventListener('dblclick', async (e) => {
               e.stopPropagation();
               const newName = await textInputModal({ title: 'Rename bucket', placeholder: 'Enter name…', defaultValue: bkt.name });
               if (!newName || newName === bkt.name) return;
               await BucketManager.rename(bkt.id, newName);
               refresh();
            });
            tab.appendChild(label);
            tab.addEventListener('click', () => switchTo(bkt.id));

            // Allow dropping bucket items onto a different tab to move them
            // NOTE: During dragenter/dragover, browsers protect dataTransfer data —
            // only .types is readable, not .getData(). So we check types instead.
            ['dragenter', 'dragover'].forEach((evt) => {
               tab.addEventListener(evt, (e) => {
                  const isBucketItem = e.dataTransfer.types && e.dataTransfer.types.includes('application/x-bucket-item-id');
                  // Only accept if dragging a bucket item and target is a different bucket
                  if (isBucketItem && bkt.id !== bucket.bucketId) {
                     e.preventDefault();
                     if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
                     tab.classList.add('dragover');
                  }
               });
            });
            tab.addEventListener('dragleave', (e) => {
               if (e.target === tab) tab.classList.remove('dragover');
            });
            tab.addEventListener('drop', async (e) => {
               const itemId = e.dataTransfer.getData('application/x-bucket-item-id');
               if (!itemId || bkt.id === bucket.bucketId) return;
               e.preventDefault();
               e.stopPropagation();
               tab.classList.remove('dragover');
               await bucket.moveItem(itemId, bkt.id);
            });

            if (bkt.id !== 'default') {
               const del = document.createElement('button');
               del.className = 'bucket-tab-del';
               del.textContent = '\u2715';
               del.title = 'Close bucket';
               del.addEventListener('click', async (e) => {
                  e.stopPropagation();
                  await BucketManager.close(bkt.id);
                  const remaining = await BucketManager.list();
                  const openRemaining = remaining.filter((b) => b.isOpen !== false);
                  const next = openRemaining[openRemaining.length - 1];
                  await switchTo(next ? next.id : 'default');
               });
               tab.appendChild(del);
            }
            tabsEl.appendChild(tab);
         }
      }

      async function switchTo(id) {
         lastBucketState = '';
         await bucket.switchBucket(id);
         try { localStorage.setItem(ACTIVE_BUCKET_KEY, id); } catch (e) {}
         const buckets = await BucketManager.list();
         renderTabs(buckets);
      }

      async function refresh() {
         const buckets = await BucketManager.list();
         renderTabs(buckets);
      }

      addBtn.addEventListener('click', async () => {
         const name = await textInputModal({
            title: 'New bucket name',
            placeholder: 'Enter name…',
            defaultValue: 'Bucket ' + (document.querySelectorAll('.bucket-tab').length + 1),
         });
         if (!name) return;
         const bkt = await BucketManager.create(name);
         await switchTo(bkt.id);
      });

      (async () => {
         let savedId = null;
         try { savedId = localStorage.getItem(ACTIVE_BUCKET_KEY); } catch (e) {}
         if (savedId) {
            const buckets = await BucketManager.list();
            const openBuckets = buckets.filter((b) => b.isOpen !== false);
            if (openBuckets.some((b) => b.id === savedId)) {
               await switchTo(savedId);
            } else {
               refresh();
            }
         } else {
            refresh();
         }
      })();

      const bucketMgrUI = createBucketManagerUI({
         BucketManager,
         bucket,
         onSwitch: () => refresh(),
         textInputModal,
      });
      const manageBtn = document.getElementById('bucket-tab-manage');
      if (manageBtn) manageBtn.addEventListener('click', () => bucketMgrUI.open());
   })();

   // ---- Outer split-grid gutter (bucket sidebar resize) ----
   setTimeout(() => {
      const gutter = document.querySelector('#wrapper .gutter-col-1, .bucket-outer > .gutter-col-1');
      if (!gutter) return;

      const STORAGE_KEY = `${appName}-bucket-width`;
      const MIN_W = 180;
      const MAX_MARGIN = 240;

      const saved = parseInt(localStorage.getItem(STORAGE_KEY) || '', 10);
      if (Number.isFinite(saved) && saved >= MIN_W) {
         document.documentElement.style.setProperty('--shared-bucket-width', saved + 'px');
      }

      let dragging = false;
      const onMove = (e) => {
         if (!dragging) return;
         const vw = window.innerWidth;
         let w = vw - e.clientX;
         const maxW = Math.max(MIN_W, vw - MAX_MARGIN);
         w = Math.max(MIN_W, Math.min(maxW, w));
         document.documentElement.style.setProperty('--shared-bucket-width', w + 'px');
      };
      const onUp = () => {
         if (!dragging) return;
         dragging = false;
         gutter.classList.remove('active');
         document.body.style.cursor = '';
         document.body.style.userSelect = '';
         window.removeEventListener('mousemove', onMove);
         window.removeEventListener('mouseup', onUp);
         const cur = getComputedStyle(document.documentElement).getPropertyValue('--shared-bucket-width').trim();
         const px = parseInt(cur, 10);
         if (Number.isFinite(px)) localStorage.setItem(STORAGE_KEY, String(px));
      };
      gutter.addEventListener('mousedown', (e) => {
         e.preventDefault();
         dragging = true;
         gutter.classList.add('active');
         document.body.style.cursor = 'col-resize';
         document.body.style.userSelect = 'none';
         window.addEventListener('mousemove', onMove);
         window.addEventListener('mouseup', onUp);
      });
   }, 100);

   // ---- Optional: handle bucket item dropped onto app area ----
   if (onItemDrop) {
      // Listen for dragstart on bucket items to set up app-area drop targets
      // This is app-specific and handled by the caller
   }

   return { bucket, exportModal, BucketManager, dbGetAll, textInputModal };
}

// ---- Shared OS file drop helper --------------------------------------------
// Detects file type from extension/MIME and adds to bucket.
// Used by initLegacyBucket and by apps with custom bucket integrations (wavedit, xtrc).
async function addOsFilesToBucket(files, bucket, appName) {
   if (!files || !files.length || !bucket) return;
   for (const file of Array.from(files)) {
      try {
         const data = await file.arrayBuffer();
         const ext = (file.name.split('.').pop() || '').toLowerCase();
         const mt = (file.type || '').toLowerCase();

         const midiExts = ['mid', 'midi'];
         const moduleExts = ['mod', 'xm', 's3m', 'it', 'mt2', 'mptm'];
         const videoExts = ['mp4', 'webm', 'ogv', 'mov', 'm4v', 'mkv', 'avi', 'flv', 'wmv'];
         const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'tiff', 'tif', 'tga', 'pcx', 'psd', 'ico', 'lbm', 'ilbm', 'svg'];
         const audioExts = ['wav', 'aif', 'aiff', 'mp3', 'ogg', 'flac', 'm4a', 'aac', 'wma', 'opus'];

         if (midiExts.includes(ext) || mt === 'audio/midi' || mt === 'audio/sp-midi') {
            await bucket.add({ source: appName, name: file.name, midiLike: data });
         } else if (moduleExts.includes(ext)) {
            await bucket.add({ source: appName, name: file.name, wavLike: data, fileType: 'module' });
         } else if (videoExts.includes(ext) || mt.startsWith('video/')) {
            await bucket.add({ source: appName, name: file.name, videoData: data, fileType: 'video' });
         } else if (imageExts.includes(ext) || mt.startsWith('image/')) {
            await bucket.add({
               source: appName, name: file.name, imageData: data,
               imageFormat: mt || `image/${ext === 'jpg' ? 'jpeg' : ext}`,
               imageMime: mt || `image/${ext === 'jpg' ? 'jpeg' : ext}`,
               fileType: 'image'
            });
         } else if (audioExts.includes(ext) || mt.startsWith('audio/')) {
            await bucket.add({ source: appName, name: file.name, wavLike: data, fileType: 'wav' });
         } else {
            await bucket.add({ source: appName, name: file.name, wavLike: data, fileType: 'raw' });
         }
      } catch (err) {
         console.error('bucket: failed to add file', file.name, err);
      }
   }
}

initLegacyBucket.addOsFilesToBucket = addOsFilesToBucket;
initLegacyBucket.styledConfirm = styledConfirm;
module.exports = initLegacyBucket;
